import type { BanDich } from '../types';
import type { viBienBan } from './bien-ban.vi';

/** Strings for the meeting minutes screens (ADR-0126). */
export const enBienBan: Pick<BanDich, keyof typeof viBienBan> = {
  bienBan: {
    tieuDe: 'Meeting minutes',
    timNhan: 'Search sessions',
    timPlaceholder: 'Search by title or file name',
    locTrangThai: 'Filter by status',
    tatCaTrangThai: 'All statuses',
    taiBanGhi: 'Upload recording',
    taiBanGhiDauTien: 'Upload your first recording',
    rongThat: 'No meeting minutes yet',
    rongThatMo: 'Upload a recording or record now to get a transcript with speakers and a summary.',
    rongLoc: 'No matching sessions',
    khongCoQuyen: 'You do not have access to meeting minutes',
    khongCoQuyenMo: 'Ask your organization admin for permission to view minutes.',
    bangCaption: 'Meeting minutes sessions',
    loiDanhSach: 'Could not load the list. {{loi}}',
    dangHien: 'Showing {{so}} sessions',
    dangHienCon: 'Showing {{so}} sessions, more available',
    cot: {
      tieuDe: 'Title',
      thoiLuong: 'Duration',
      soNguoiNoi: 'Speakers',
      trangThai: 'Status',
      nguoiTao: 'Created by',
      ngayTao: 'Created on',
    },
    trangThai: {
      QUEUED: 'Queued',
      TRANSCRIBING: 'Transcribing',
      SUMMARIZING: 'Summarizing',
      DONE: 'Done',
      FAILED: 'Failed',
    },
    tomTatLoiNgan: 'Summary failed',
    moPhien: 'View minutes',
    doiTen: 'Rename session',
    thuLaiPhien: 'Retry',
    xuatWord: 'Export Word',
    xuatVanBan: 'Export text',
    xoaPhien: 'Delete session',
    daXuat: 'Downloaded {{ten}}',
    loiXuat: 'Could not download the file. {{loi}}',
    daXoa: 'Deleted session {{ten}}.',
    daDoiTen: 'Session renamed.',
    daThuLai: 'Session queued for reprocessing.',
    xacNhanXoa: {
      tieuDe: 'Delete session "{{ten}}"?',
      than: 'The recording, transcript and summary of this session will be permanently deleted. This cannot be undone.',
      nut: 'Delete session',
    },
    hopDoiTen: {
      tieuDe: 'Rename session',
      nhan: 'Title',
      nut: 'Save title',
      loiTrong: 'Enter a title for the session.',
    },
    chiTiet: {
      veDanhSach: 'Meeting minutes',
      loiTai: 'Could not load the session. {{loi}}',
      khongThay: 'Session not found',
      khongThayMo: 'It may have been deleted.',
      thongTin: '{{thoiLuong}} · {{ngay}}',
    },
    phat: {
      nhan: 'Recording player',
      phat: 'Play',
      tamDung: 'Pause',
      lui: 'Back 5 seconds',
      toi: 'Forward 5 seconds',
      thanhTua: 'Playback position',
      tocDo: 'Playback speed',
      phimTat: 'Shortcuts: K play or pause, J back 5 seconds, L forward 5 seconds.',
      khongPhatDuoc: 'Could not play the recording. The transcript is still available.',
      loiTai: 'Could not get the recording. {{loi}} The transcript is still available.',
    },
    loi: {
      tieuDe: 'Transcript',
      vungCuon: 'Transcript content',
      rong: 'No transcript yet',
      rongXuLy: 'The transcript appears here once it is ready',
      loiTai: 'Could not load the transcript. {{loi}}',
      canhBao: 'Speaker labels may be inaccurate. Listen back to check',
      veDoanDangPhat: 'Back to current segment',
      tuaToi: 'Play from {{tg}}',
      suaDoan: 'Edit segment at {{tg}}',
      daSua: 'Edited',
      nhanNoiDung: 'Segment text',
      nhanNguoiNoi: 'Speaker of this segment',
      luuDoan: 'Save segment',
      loiTrong: 'Enter text for this segment, or Cancel to keep it.',
      daLuuDoan: 'Segment saved.',
      chuaRo: 'Unknown speaker',
      nguoiNoiSo: 'Speaker {{so}}',
    },
    nguoiNoi: {
      tieuDe: 'Speakers',
      rong: 'Speakers appear here once the transcript is ready',
      nhanTen: 'Name of {{nhan}}',
      datTen: 'Add a name',
      noiTrong: 'Speaking time',
      daLuu: 'Speaker name updated.',
    },
  },
  bienBanTai: {
    tieuDe: 'Upload recording',
    theTep: 'Choose file',
    theGhiAm: 'Record now',
    keoTha: 'Drag a file here or click to choose',
    keoThaMo: 'Audio or video, up to {{so}} MB',
    boTep: 'Remove file {{ten}}',
    nhanTieuDe: 'Title',
    nhanNgonNgu: 'Language',
    ngonNgu: { auto: 'Detect automatically', vi: 'Vietnamese', en: 'English' },
    nhanSoNguoi: 'Number of speakers',
    goiYSoNguoi: 'Leave blank if unsure. The right number makes speaker labels more accurate.',
    loiChuaChon: 'Choose a file or make a recording before sending.',
    loiTrongTieuDe: 'Enter a title for the session.',
    loiSaiDinhDang: 'This file is not audio or video. Choose an MP3, WAV, M4A, AAC, OGG, OPUS, FLAC, WEBM, MP4 or MOV file.',
    loiQuaLon: 'The file is larger than {{so}} MB. Split or compress it, then upload again.',
    loiTepRong: 'The file is empty. Check it and choose it again.',
    gui: 'Send recording',
    dangGui: 'Sending...',
    tienTrinh: 'Uploading {{so}}%',
    nhanTienTrinh: 'Upload progress',
    huyTai: 'Cancel upload',
    daGui: 'Recording sent. Processing has started.',
    tieuDeGhiAm: 'Recording {{luc}}',
  },
  bienBanGhiAm: {
    batDau: 'Start recording',
    dangXinQuyen: 'Requesting microphone access...',
    dung: 'Stop recording',
    lamLai: 'Record again',
    dangGhi: 'Recording',
    daGhi: 'Recorded {{tg}}. Listen back before sending.',
    nhanNgheLai: 'Listen to the recording',
    loi: {
      KHONG_HO_TRO: 'This browser cannot record audio. Use a recent Chrome, Edge, Firefox or Safari, or upload an existing recording.',
      KHONG_BAO_MAT: 'Recording only works over HTTPS. Reopen this page with an https:// address, or upload an existing recording.',
      TU_CHOI: 'Microphone access is blocked. Click the lock icon in the address bar, allow the microphone, then try again.',
      KHONG_CO_THIET_BI: 'No microphone found. Plug one in and try again, or upload an existing recording.',
      DANG_BAN: 'Another app is using the microphone. Close that app, then try again.',
      KHONG_CO_TIENG: 'The recording has no data. Record again, or upload an existing recording.',
      KHAC: 'Could not open the microphone. Try again, or upload an existing recording.',
    },
  },
  bienBanXuLy: {
    nhan: 'Processing progress',
    buoc: {
      QUEUED: 'Queued',
      TRANSCRIBING: 'Transcribe',
      SUMMARIZING: 'Summarize',
      DONE: 'Done',
    },
    moTa: {
      QUEUED: 'This session is in the queue. Files are processed one at a time, and this page updates by itself.',
      TRANSCRIBING: 'Transcribing and identifying speakers. Long files can take a few minutes, and this page updates by itself.',
      SUMMARIZING: 'The transcript is ready. Writing the summary.',
    },
    loiMacDinh: 'Processing failed. Try again in a few minutes.',
    tieuDeLoi: 'Processing failed',
    maLoi: 'Error code: {{ma}}',
    thuLai: 'Retry processing',
  },
  bienBanTomTat: {
    tieuDe: 'Summary',
    trangThai: {
      NONE: 'None',
      PENDING: 'Summarizing',
      DONE: 'Ready',
      FAILED: 'Failed',
    },
    daSuaTay: 'Edited by hand',
    tongQuan: 'Overview',
    yChinh: 'Key points',
    quyetDinh: 'Decisions',
    viecCanLam: 'Action items',
    phuTrach: 'Owner: {{ten}}',
    han: 'Due: {{han}}',
    khongCoMuc: 'None',
    suaTay: 'Edit summary',
    tomTatLai: 'Summarize again',
    taoTomTat: 'Create summary',
    dangTomTat: 'Writing the summary. This page updates by itself.',
    choChepLoi: 'The summary appears after the transcript is ready',
    chuaCo: 'This session has no summary yet',
    loiMacDinh: 'The summary failed. Click Summarize again to retry.',
    daGuiTomTatLai: 'Summary requested.',
    daLuu: 'Summary saved.',
    xacNhanLai: {
      tieuDe: 'Summarize this session again?',
      than: 'Your edited summary will be replaced by a new generated one. Your edits cannot be recovered.',
      nut: 'Summarize again',
    },
    hop: {
      tieuDe: 'Edit summary',
      tongQuan: 'Overview',
      yChinh: 'Key points',
      quyetDinh: 'Decisions',
      moiDong: 'One item per line.',
      viecCanLam: 'Action items',
      noiDungViec: 'Text of item {{so}}',
      nguoiPhuTrach: 'Owner of item {{so}}',
      hanViec: 'Due date of item {{so}}',
      phViec: 'Action item',
      phNguoi: 'Owner',
      phHan: 'Due',
      themViec: 'Add item',
      boViec: 'Remove item {{so}}',
      loiTongQuan: 'Enter an overview for the minutes.',
      loiViec: 'Enter the item text, or remove this row.',
      nut: 'Save summary',
    },
  },
};
